import {useCallback, useState} from 'react'

import {isLoggedIn} from '@github-ui/client-env'
import type {HeaderPageData} from '../page-data/payloads/header'
import {ActionList, ActionMenu, Button, Label, Link, Popover} from '@primer/react'
import {ArrowSwitchIcon, ArrowUpRightIcon, CommentIcon, RocketIcon} from '@primer/octicons-react'
import {toggleGenericFeaturePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {NavigationUrls} from '../types/navigation-urls-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useDismissNotice, useIsNoticeDismissed} from '../hooks/use-user-notices'
import styles from './FilesChangedFeedback.module.css'
import {clsx} from 'clsx'
import {Notices, type FilesPageNotices, type UserNotice} from '../page-data/payloads/files'
import {FilesChangedRouteDataFetcher} from './FilesChangedRouteDataFetcher'

export function FilesChangedFeedback({
  repository,
  urls,
  renderLocation,
}: {
  repository: HeaderPageData['repository']
  urls: NavigationUrls
  renderLocation: 'mobile' | 'desktop'
}) {
  const onClick = async () => {
    const formData = new FormData()
    formData.append('feature_name', 'prx_files')

    await verifiedFetch(toggleGenericFeaturePath({repo: repository}), {
      body: formData,
      method: 'POST',
    })
  }

  const shouldShowLoggedInOptions = isLoggedIn()

  const pathname = ssrSafeLocation.pathname ?? ''
  const search = ssrSafeLocation.search ?? ''
  let switchBackUrl: string
  if (pathname) {
    switchBackUrl = `${pathname}${search ? `${search}&` : '?'}new_files_changed=false`
  } else {
    // Fallback to NavigationUrls for SSR or when location is unavailable
    switchBackUrl = `${urls.files}?new_files_changed=false`
  }

  return (
    <div className="position-relative flex-order-2 flex-md-order-none pl-1">
      <ActionMenu>
        <ActionMenu.Button variant="invisible" size="small">
          <Label variant="success" className={styles.PreviewLabel}>
            Preview
          </Label>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.LinkItem
              href="https://gh.io/new-files-changed-changelog"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ActionList.LeadingVisual>
                <RocketIcon />
              </ActionList.LeadingVisual>
              What&apos;s new
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            <ActionList.LinkItem
              href="https://gh.io/new-files-changed-feedback"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Give feedback
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            {shouldShowLoggedInOptions && (
              <>
                <ActionList.Divider />
                <ActionList.LinkItem
                  href={switchBackUrl}
                  onClick={async e => {
                    e.preventDefault()
                    await onClick()
                    window.location.href = switchBackUrl
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ArrowSwitchIcon />
                  </ActionList.LeadingVisual>
                  Switch to the classic experience
                </ActionList.LinkItem>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <FilesChangedOptOutPopoverWrapper renderLocation={renderLocation} />
    </div>
  )
}

/**
 * Wrapper that renders the popover with data from whichever files changed route is active.
 */
function FilesChangedOptOutPopoverWrapper({renderLocation}: {renderLocation: 'mobile' | 'desktop'}) {
  return (
    <FilesChangedRouteDataFetcher readFromChildRoute>
      {data => <FilesChangedOptOutPopover userNotices={data?.userNotices} renderLocation={renderLocation} />}
    </FilesChangedRouteDataFetcher>
  )
}

function FilesChangedOptOutPopover({
  userNotices,
  renderLocation,
}: {
  userNotices?: Array<UserNotice<FilesPageNotices>>
  renderLocation: 'mobile' | 'desktop'
}) {
  const isNoticeDismissed = useIsNoticeDismissed(Notices.FilesChangedOptOut, userNotices)
  const {dismissNotice} = useDismissNotice(Notices.FilesChangedOptOut, userNotices)
  const caretPosition = renderLocation === 'mobile' ? 'top-left' : 'top'

  const [popoverOpen, setPopoverOpen] = useState(true)

  const dismissPopover = useCallback(() => {
    setPopoverOpen(false)
    dismissNotice()
  }, [dismissNotice])

  if (isNoticeDismissed) return null

  return (
    <Popover
      open={popoverOpen}
      caret={caretPosition}
      className={clsx(styles.PopoverContainer, {
        [styles.PopoverContainerDesktop]: renderLocation === 'desktop',
        [styles.PopoverContainerMobile]: renderLocation === 'mobile',
      })}
    >
      <Popover.Content className={clsx('d-flex flex-column gap-2', styles.PopoverContent)}>
        <h2 className={clsx('lh-default', styles.Heading)}>Welcome to the upgraded Files Changed experience</h2>
        <p className="color-fg-muted f5 text-normal lh-default">
          It&apos;s faster, more accessible, and includes new features like commenting anywhere in changed files and
          file tree indicators.
        </p>
        <div className="d-flex gap-2 flex-row w-full flex-wrap">
          <Button
            as={Link}
            trailingVisual={<ArrowUpRightIcon />}
            target="_blank"
            rel="noopener noreferrer"
            href="https://gh.io/new-files-changed-changelog"
            onClick={dismissPopover}
          >
            See what&apos;s new
          </Button>
          <Button onClick={dismissPopover} variant="invisible">
            Dismiss
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}

try{ FilesChangedFeedback.displayName ||= 'FilesChangedFeedback' } catch {}
try{ FilesChangedOptOutPopoverWrapper.displayName ||= 'FilesChangedOptOutPopoverWrapper' } catch {}
try{ FilesChangedOptOutPopover.displayName ||= 'FilesChangedOptOutPopover' } catch {}